import '../styles/AssetLibrary.css';
import '../styles/AssetUploadDialog.css';

import Dropzone, { Accept } from 'react-dropzone';
import React, { ChangeEvent, useState } from 'react';

import { AssetPreview } from './AssetPreview';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { isEmpty } from 'lodash';

interface AssetUploadDialogProps {
  title?: string;
  accept: string;
  multiple: boolean;
  onUpload: (files: File[]) => void;
  onClose: () => void;
}

const AssetUploadDialog = (props: AssetUploadDialogProps) => {
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

  function handleFilesSelect(files: File[]) {
    setFilesToUpload(files);
  }

  function handleFileDrop(files: File[]) {
    setFilesToUpload(files);
  }

  function handleUpload() {
    props.onUpload(filesToUpload);
  }

  return (
    <>
      <Dialog open>
        <DialogTitle>
          <span>{props.title || 'Upload File'}</span>
          <div className="close-button">
            <IconButton onClick={props.onClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <Grid container style={{ flex: 1, height: '100%' }}>
            <AssetPreviewPanel
              accept={props.accept}
              multiple={props.multiple}
              onSelect={handleFilesSelect}
              onDrop={handleFileDrop}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!filesToUpload.length}
            onClick={handleUpload}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface AssetPreviewPanelProps {
  accept: string;
  multiple: boolean;
  onSelect: (files: File[]) => void;
  onDrop: (files: File[]) => void;
}

function AssetPreviewPanel(props: AssetPreviewPanelProps) {
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

  function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files);
    props.onSelect(files);
    setFilesToUpload(files);
  }

  function handleFileDrop(files: File[]) {
    props.onDrop(files);
    setFilesToUpload(files);
  }

  // Convert the accept string to the Accept type
  const accept: Accept = {
    accept: [props.accept]
  };

  return isEmpty(filesToUpload) ? (
    <Dropzone accept={accept} onDrop={handleFileDrop}>
      {({ getRootProps, getInputProps }) => (
        <div {...getRootProps()} className="preview-panel">
          <input
            {...getInputProps()}
            type="file"
            name="file"
            id="select-file"
            accept={props.accept}
            multiple={props.multiple}
            onChange={handleFileSelect}
          />
          <Grid
            container
            direction="column"
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ width: '100%' }}
          >
            <Grid>
              <CloudUploadIcon fontSize="large" />
            </Grid>
            <Grid>
              <Typography variant="body1">Drag and drop a file</Typography>
            </Grid>
            <Grid>
              <Typography variant="body1">or</Typography>
            </Grid>
            <Grid>
              <Button variant="contained" color="primary">
                Select File
              </Button>
            </Grid>
          </Grid>
        </div>
      )}
    </Dropzone>
  ) : (
    //* Preview asset
    <AssetPreviewer files={filesToUpload} />
  );
}
interface AssetPreviewerProps {
  files: File[];
}

function AssetPreviewer(props: AssetPreviewerProps) {
  return (
    <Grid container>
      {props.files.map((file) => (
        <Grid key={file.name}>
          <AssetPreview file={file} className="asset-preview" />
        </Grid>
      ))}
    </Grid>
  );
}

export default AssetUploadDialog;
